import { useMemo, useState } from "react";
import { useHistory } from "react-router";
import axios from "axios";
import moment from "moment";
import {
	Box, CircularProgress, Grid, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography
} from "@mui/material";
import Layout from "../../../Components/Layout/Layout";
import CompaniesNavigation from "Navigation/CompaniesNavigation";
import PaperTable from "../../../Components/PaperTable";
import Tile from "Components/Tiles/Tile";
import useAsyncEffect from "../../../system/useAsyncEffect";
import useCompanies from "../../../system/useCompanies";
import dateFormats from "system/dateFormats";
import { TransportPriceCarrierOverview, TransportPriceOverview } from "../../../system/Domain";
import { axleLabels } from "./transportPriceLabels";

const number = new Intl.NumberFormat("de-DE");

const Stat = ({ title, value }: { title: string; value: string }) => (
	<Tile title={title}>
		<Typography variant="h4" component="div">{value}</Typography>
	</Tile>
);

const TransportPricesPage = () => {
	const history = useHistory();
	const [companies] = useCompanies();
	const [overview, setOverview] = useState<TransportPriceOverview>();
	const today = moment().format("YYYY-MM-DD");
	const tomorrow = moment().add(1, "day").format("YYYY-MM-DD");
	const [date, setDate] = useState(today);

	useAsyncEffect(async (cancelToken) => {
		const { data } = await axios.get<TransportPriceOverview>("/api/transport-prices/overview", {
			params: { date },
			cancelToken: cancelToken.getAxiosToken()
		});

		setOverview(data);
	}, [date]);

	// Active carriers without any price list are listed as well, so it is visible who is still missing.
	const rows = useMemo<TransportPriceCarrierOverview[]>(() => {
		if (!overview) {
			return [];
		}

		const missing = (companies ?? [])
			.filter(c => c.transports?.active && !overview.carriers.some(o => o.carrier.id === c.id))
			.map<TransportPriceCarrierOverview>(c => ({
				carrier: { id: c.id, name: c.name },
				lists: 0, validLists: 0, validAxles: [], validRoutes: 0, validPrices: 0
			}));

		return [...overview.carriers, ...missing].sort((a, b) => a.carrier.name.localeCompare(b.carrier.name));
	}, [overview, companies]);

	return (
		<Layout
			title="Frachtpreise"
			search="disabled"
			navigation={<CompaniesNavigation />}
		>
			{!overview && <Box mt={2}><CircularProgress size={48} /></Box>}
			{overview && (
				<>
					<Box mt={2}>
						<Grid container spacing={2}>
							<Grid item xs={12} md={3}>
								<Stat title="Frachtführer mit gültigen Preisen" value={String(rows.filter(r => r.validLists > 0).length)} />
							</Grid>
							<Grid item xs={12} md={3}>
								<Stat title="Gültige Strecken" value={number.format(rows.reduce((s, r) => s + r.validRoutes, 0))} />
							</Grid>
							<Grid item xs={12} md={3}>
								<Stat title="Gültige Preise" value={number.format(rows.reduce((s, r) => s + r.validPrices, 0))} />
							</Grid>
							<Grid item xs={12} md={3}>
								<Tile title="Stichtag">
									<Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
										<ToggleButtonGroup
											exclusive
											size="small"
											color="primary"
											value={date === today ? "today" : date === tomorrow ? "tomorrow" : null}
											onChange={(_, value) => value && setDate(value === "today" ? today : tomorrow)}
										>
											<ToggleButton value="today">Heute</ToggleButton>
											<ToggleButton value="tomorrow">Morgen</ToggleButton>
										</ToggleButtonGroup>
										<TextField
											variant="standard"
											type="date"
											value={date}
											onChange={e => e.target.value && setDate(e.target.value)}
											inputProps={{ "aria-label": "Stichtag" }}
											sx={{ width: 140 }}
										/>
									</Box>
								</Tile>
							</Grid>
						</Grid>
					</Box>
					<Box mt={2}>
						<PaperTable>
							<TableHead>
								<TableRow>
									<TableCell>Frachtführer</TableCell>
									<TableCell>Gültige Achsen</TableCell>
									<TableCell align="right">Strecken</TableCell>
									<TableCell align="right">Preise</TableCell>
									<TableCell>Frühestes Ende</TableCell>
									<TableCell align="right">Preislisten gesamt</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.map(r => (
									<TableRow
										key={r.carrier.id}
										hover
										onClick={() => history.push(`/companies/transport-prices/${r.carrier.id}`)}
									>
										<TableCell>{r.carrier.name}</TableCell>
										<TableCell>{r.validAxles.length ? r.validAxles.map(a => axleLabels[a]).join(", ") : "–"}</TableCell>
										<TableCell align="right">{number.format(r.validRoutes)}</TableCell>
										<TableCell align="right">{number.format(r.validPrices)}</TableCell>
										<TableCell>{r.validUntil ? moment(r.validUntil).format(dateFormats.date) : r.validLists ? "unbefristet" : "–"}</TableCell>
										<TableCell align="right">{r.lists}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</PaperTable>
						{rows.length === 0 && <Typography mt={2}>Es sind noch keine Frachtpreise angelegt.</Typography>}
					</Box>
					<Box mt={2}>
						<Tile title="Preisabfrage">
							<Typography color="text.secondary">
								Hier folgt die Suche nach Strecken und die Preisermittlung anhand eines Beispielauftrags.
							</Typography>
						</Tile>
					</Box>
				</>
			)}
		</Layout>
	);
};

export default TransportPricesPage;
